import type { Contract } from "@prisma/client";
import posthog from "posthog-js";

export const WALES_BETA_COHORT = "wales_2026_poc";

type DataCompletenessProperties = {
  annual_pay_valid: boolean;
  data_complete: boolean;
  membership_id_valid: boolean;
  weekly_hours_valid: boolean;
};

type ActionFailureProperties = {
  contract: Contract;
  error_code: string;
  operation:
    | "create"
    | "update"
    | "delete"
    | "assign"
    | "change"
    | "remove"
    | "save"
    | "download";
  rotation_id?: string;
  surface:
    | "rotation"
    | "shift_type"
    | "rota"
    | "work_schedule"
    | "advanced_information"
    | "report_pdf";
};

export type WalesBetaAnalyticsEvents = {
  wales_beta_rotation_form_opened: {
    contract: Contract;
  };
  wales_beta_rotation_created: {
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_rota_opened: {
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_shift_type_created: {
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_shift_type_updated: {
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_shift_type_deleted: {
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_rota_day_assigned: {
    assigned_day_count: number;
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_rota_day_changed: {
    assigned_day_count: number;
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_rota_day_removed: {
    assigned_day_count: number;
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_work_schedule_opened: {
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_work_schedule_saved: {
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_advanced_information_opened: {
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_advanced_information_saved: {
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_validation_failed: {
    contract: Contract;
    form: "rotation" | "shift_type" | "work_schedule" | "advanced_information";
    invalid_fields: string;
    rotation_id?: string;
  };
  wales_beta_action_failed: ActionFailureProperties;
  wales_beta_report_opened: DataCompletenessProperties & {
    contract: Contract;
    rotation_id: string;
  };
  wales_beta_report_pdf_downloaded: DataCompletenessProperties & {
    contract: Contract;
    rotation_id: string;
  };
};

export function getWalesBetaAnalyticsErrorCode(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "UNKNOWN";
  }

  const candidate = error as {
    code?: unknown;
    data?: { code?: unknown };
  };
  const code = candidate.data?.code ?? candidate.code;
  return typeof code === "string" && /^[A-Z][A-Z0-9_]*$/.test(code)
    ? code
    : "UNKNOWN";
}

let activeMembershipId: string | null = null;

export function countAssignedRotaDays<T extends object>(
  days: ReadonlyArray<T>,
): number {
  return days.filter(
    (day) => !("status" in day) || day.status !== "loading",
  ).length;
}

export function isWalesBetaAnalyticsEligible({
  authenticated,
  membershipId,
  walesEnabled,
}: {
  authenticated: boolean;
  membershipId: string | null | undefined;
  walesEnabled: boolean;
}): boolean {
  return authenticated && walesEnabled && Boolean(membershipId);
}

export function initializeWalesBetaAnalytics(membershipId: string): boolean {
  if (process.env.NODE_ENV === "test" || typeof window === "undefined") {
    return false;
  }

  const projectToken = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  if (!projectToken || !host) {
    return false;
  }

  try {
    if (!posthog.__loaded) {
      posthog.init(projectToken, {
        api_host: host,
        autocapture: true,
        bootstrap: {
          distinctID: membershipId,
          isIdentifiedID: true,
        },
        capture_pageleave: true,
        capture_pageview: "history_change",
        disable_session_recording: false,
        person_profiles: "identified_only",
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "[data-ph-sensitive]",
        },
      });
    }

    if (activeMembershipId && activeMembershipId !== membershipId) {
      posthog.stopSessionRecording();
      posthog.reset();
    }

    if (posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing({ captureEventName: false });
    }

    posthog.identify(membershipId, {
      beta_cohort: WALES_BETA_COHORT,
    });
    posthog.register({ beta_cohort: WALES_BETA_COHORT });
    posthog.startSessionRecording({
      sampling: true,
      linked_flag: true,
      url_trigger: true,
      event_trigger: true,
    });
    activeMembershipId = membershipId;
    return true;
  } catch {
    activeMembershipId = null;
    try {
      posthog.stopSessionRecording();
      posthog.reset();
      posthog.opt_out_capturing();
    } catch {
      // The integration remains disabled if PostHog cannot initialize cleanly.
    }
    return false;
  }
}

export function captureWalesBetaEvent<
  EventName extends keyof WalesBetaAnalyticsEvents,
>(event: EventName, properties: WalesBetaAnalyticsEvents[EventName]): void {
  if (!activeMembershipId) {
    return;
  }

  try {
    posthog.capture(event, properties);
  } catch {
    // Analytics must never interrupt a user workflow.
  }
}

export function resetWalesBetaAnalytics(): void {
  if (!activeMembershipId && !posthog.__loaded) {
    return;
  }

  activeMembershipId = null;
  try {
    posthog.stopSessionRecording();
    posthog.reset();
    posthog.opt_out_capturing();
  } catch {
    // Logout and session changes must continue even if analytics fails.
  }
}
